import { useEffect, useState } from "react";
import { listCustomerUsers } from "../erpClient";
import { extractCustomerUsersArray } from "./customerUsersErpAdapter";
import { isValidMobileNumber } from "./mobileInput";

const DEFAULT_MESSAGE = "Mobile number already exists";
/** Stable default so the effect does not re-run every render when callers omit localUsers. */
const EMPTY_LOCAL_USERS = [];

function customerMatchesMobile(row, mobile) {
  return String(row?.mobile ?? "").trim() === mobile;
}

function isTempCustomerId(id) {
  return String(id ?? "").startsWith("TEMP_");
}

function isExcludedCustomer(row, excludeCustomerId) {
  if (excludeCustomerId == null || excludeCustomerId === "") return false;
  return String(row?.id) === String(excludeCustomerId);
}

/** Draft/temp rows are local-only; they must not count as an existing customer. */
function isDraftLocalCustomer(row) {
  return isTempCustomerId(row?.id);
}

export function useMobileExistsCheck(
  mobile,
  {
    enabled = true,
    excludeCustomerId = null,
    excludeMobile = null,
    localUsers = EMPTY_LOCAL_USERS,
  } = {},
) {
  const [exists, setExists] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setExists(false);
      setChecking(false);
      return;
    }

    const trimmed = String(mobile ?? "").trim();
    if (!isValidMobileNumber(trimmed)) {
      setExists(false);
      setChecking(false);
      return;
    }

    if (excludeMobile && trimmed === String(excludeMobile).trim()) {
      setExists(false);
      setChecking(false);
      return;
    }

    const users = Array.isArray(localUsers) ? localUsers : EMPTY_LOCAL_USERS;
    const isRealExcludeId =
      excludeCustomerId != null &&
      excludeCustomerId !== "" &&
      !isTempCustomerId(excludeCustomerId);

    // Editing an existing customer's own number — skip duplicate flag (no API call).
    if (isRealExcludeId) {
      const selected = users.find(
        (row) => String(row.id) === String(excludeCustomerId),
      );
      if (selected && customerMatchesMobile(selected, trimmed)) {
        setExists(false);
        setChecking(false);
        return;
      }
    }

    // Only real (non-TEMP) local customers can trigger "already exists".
    const localMatch = users.some(
      (row) =>
        customerMatchesMobile(row, trimmed) &&
        !isDraftLocalCustomer(row) &&
        !isExcludedCustomer(row, excludeCustomerId),
    );

    if (localMatch) {
      setExists(true);
      setChecking(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const response = await listCustomerUsers({ search: trimmed });
        if (cancelled) return;

        const rows = extractCustomerUsersArray(response) || [];
        // TEMP_ ids are never on the server — only exclude a real ERP customer id.
        const remoteExcludeId = isRealExcludeId ? excludeCustomerId : null;
        const remoteMatch = rows.some(
          (row) =>
            customerMatchesMobile(row, trimmed) &&
            !isExcludedCustomer(row, remoteExcludeId),
        );

        setExists(remoteMatch);
      } catch {
        if (!cancelled) setExists(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mobile, enabled, excludeCustomerId, excludeMobile, localUsers]);

  return {
    exists,
    checking,
    message: exists ? DEFAULT_MESSAGE : "",
  };
}
